import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  closeThanksWidget,
  configureThanksWidget,
  displayThanksWidget,
} from '@thanksjs/web'

import { Alert } from 'app/components/design-system-components/Alert'
import {
  getThanksPartnerId,
  THANKS_DISPLAY_MODE_BY_PLACEMENT,
} from 'app/Integrations/components/Thanks/config'
import { useThanksConfig } from 'app/Integrations/components/Thanks/hooks/useThanksConfig'
import { isInThanksSample } from 'app/Integrations/components/Thanks/sampling'
import {
  ThanksDevPreviewCard,
  ThanksDevPreviewDescription,
  ThanksDevPreviewOfferItem,
  ThanksDevPreviewOfferList,
  ThanksDevPreviewTitle,
  ThanksEmbedContainer,
  ThanksEmbedSection,
} from 'app/Integrations/components/Thanks/ThanksWidget.styles'
import type { ThanksWidgetProps } from 'app/Integrations/components/Thanks/types'
import {
  GTM_THANKS_WIDGET_CLOSED,
  GTM_THANKS_WIDGET_DISPLAYED,
} from 'app/utils/google-tag-manager/helpers'

type ThanksWidgetState =
  | 'initializing'
  | 'missingPartnerId'
  | 'missingSdk'
  | 'devPreview'
  | 'loaded'

const isDevelopmentPreviewEnabled = process.env.REACT_APP_ENV === 'development'

const DEV_PREVIEW_OFFERS = {
  applicationSubmitted: [
    'Move-in utility setup discount',
    'Internet plan sign-up bonus',
    'Furniture rental welcome offer',
  ],
  viewingRegistration: [
    'Weekend moving truck special',
    'Storage unit first-month offer',
    'Renter contents insurance starter deal',
  ],
}

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[]
  }
}

const pushThanksTrackingEvent = (
  eventName: string,
  placement: string,
): void => {
  const { dataLayer } = window
  if (Array.isArray(dataLayer)) {
    dataLayer.push({ event: eventName, placement })
  }
}

export default function ThanksWidget({
  placement,
  sampleSeed,
  sampleRate,
  className = '',
}: ThanksWidgetProps) {
  const partnerId = getThanksPartnerId(placement)
  const isEmbeddedMode =
    THANKS_DISPLAY_MODE_BY_PLACEMENT[placement] === 'embedded'

  const [widgetState, setWidgetState] = useState<ThanksWidgetState>(() => {
    if (!partnerId) {
      return isDevelopmentPreviewEnabled ? 'devPreview' : 'missingPartnerId'
    }
    return 'initializing'
  })
  const [containerElement, setContainerElement] =
    useState<HTMLDivElement | null>(null)
  const hasUserInteractedWithInlineOffer = useRef(false)
  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    setContainerElement(node)
  }, [])
  const trackDisplayed = useCallback(
    () => pushThanksTrackingEvent(GTM_THANKS_WIDGET_DISPLAYED, placement),
    [placement],
  )
  const trackClosed = useCallback(
    () => pushThanksTrackingEvent(GTM_THANKS_WIDGET_CLOSED, placement),
    [placement],
  )
  const upscaleInlineThanksEmbed = useCallback(() => {
    if (
      !isEmbeddedMode ||
      placement !== 'applicationSubmitted' ||
      !containerElement
    ) {
      return
    }

    const directChildren = Array.from(
      containerElement.children,
    ) as HTMLElement[]
    directChildren.forEach((child) => {
      if (child.tagName === 'IFRAME') return
      if (child.dataset.snugUpscaledThanksEmbed === 'true') return
      child.style.setProperty('display', 'block')
      child.style.setProperty('width', '100%')
      child.style.setProperty('max-width', '100%')
      child.style.setProperty('min-width', '100%')
      child.dataset.snugUpscaledThanksEmbed = 'true'
    })

    const inlineIframes = Array.from(
      containerElement.querySelectorAll('iframe'),
    ) as HTMLIFrameElement[]

    inlineIframes.forEach((iframe) => {
      const iframeSrc = iframe.src.toLowerCase()
      const isPreloadIframe = iframeSrc.includes('thanks.is/preload')
      if (isPreloadIframe) {
        iframe.style.setProperty('display', 'none')
        iframe.style.setProperty('width', '0')
        iframe.style.setProperty('height', '0')
        iframe.style.setProperty('border', '0')
        iframe.style.setProperty('pointer-events', 'none')
        return
      }

      const isInlineEmbedIframe = iframeSrc.includes('thanks.is/embed')
      if (!isInlineEmbedIframe) return
      if (iframe.dataset.snugUpscaledThanksEmbed === 'true') return
      iframe.style.setProperty('display', 'block')
      iframe.style.setProperty('width', '100%')
      iframe.style.setProperty('max-width', '100%')
      iframe.style.setProperty('min-width', '100%')
      iframe.style.setProperty('outline', 'none')
      iframe.style.setProperty(
        'height',
        `${Math.max(iframe.offsetHeight, 150)}px`,
      )
      iframe.dataset.snugUpscaledThanksEmbed = 'true'
    })

    const currentMinHeight = parseFloat(
      window.getComputedStyle(containerElement).minHeight || '0',
    )
    const minimumHeight = 150
    if (minimumHeight > currentMinHeight) {
      containerElement.style.setProperty('min-height', `${minimumHeight}px`)
    }
    containerElement.style.setProperty('overflow', 'visible')
  }, [containerElement, isEmbeddedMode, placement])

  const suppressFullscreenThanksOverlays = useCallback(() => {
    if (!isEmbeddedMode) return

    const thanksIframes = Array.from(
      document.querySelectorAll('iframe'),
    ).filter((iframe) => iframe.src.toLowerCase().includes('thanks'))

    const activeElement = document.activeElement
    if (
      activeElement instanceof HTMLIFrameElement &&
      containerElement?.contains(activeElement)
    ) {
      hasUserInteractedWithInlineOffer.current = true
    }

    if (hasUserInteractedWithInlineOffer.current) {
      thanksIframes.forEach((iframe) => {
        if (iframe.dataset.snugSuppressedOverlay !== 'true') return
        iframe.style.removeProperty('display')
        iframe.style.removeProperty('visibility')
        iframe.style.removeProperty('pointer-events')
        iframe.removeAttribute('aria-hidden')
        delete iframe.dataset.snugSuppressedOverlay
      })
      return
    }

    thanksIframes.forEach((iframe) => {
      const style = window.getComputedStyle(iframe)
      const rect = iframe.getBoundingClientRect()
      const isLargeOverlay =
        rect.width >= window.innerWidth * 0.7 &&
        rect.height >= window.innerHeight * 0.5
      const isOverlayPosition =
        style.position === 'fixed' || style.position === 'absolute'

      if (!isLargeOverlay || !isOverlayPosition) return

      iframe.style.setProperty('display', 'none', 'important')
      iframe.style.setProperty('visibility', 'hidden', 'important')
      iframe.style.setProperty('pointer-events', 'none', 'important')
      iframe.setAttribute('aria-hidden', 'true')
      iframe.dataset.snugSuppressedOverlay = 'true'
    })
  }, [containerElement, isEmbeddedMode])

  const clearStaleInteractionLocks = useCallback(() => {
    if (!isEmbeddedMode) return

    const releaseLocks = (element: HTMLElement | null) => {
      if (!element) return
      if (element.style.touchAction === 'none') {
        element.style.removeProperty('touch-action')
      }
      if (element.style.pointerEvents === 'none') {
        element.style.removeProperty('pointer-events')
      }
      if (element.style.overflow === 'hidden') {
        element.style.removeProperty('overflow')
      }
      if (element.inert) {
        element.inert = false
      }
      if (element.hasAttribute('inert')) {
        element.removeAttribute('inert')
      }
    }

    releaseLocks(document.body)
    releaseLocks(document.documentElement)
    releaseLocks(document.getElementById('index'))
    releaseLocks(document.getElementById('app-scrollable-container'))
  }, [isEmbeddedMode])

  const applyThanksPresentationGuards = useCallback(() => {
    suppressFullscreenThanksOverlays()
    upscaleInlineThanksEmbed()
    clearStaleInteractionLocks()
  }, [
    suppressFullscreenThanksOverlays,
    upscaleInlineThanksEmbed,
    clearStaleInteractionLocks,
  ])
  const thanksConfig = useThanksConfig({
    placement,
    embeddedInto: isEmbeddedMode ? containerElement : null,
    onDisplay: trackDisplayed,
    onClose: trackClosed,
  })

  const isInSample = useMemo(() => {
    return isInThanksSample(sampleSeed, sampleRate)
  }, [sampleSeed, sampleRate])

  useEffect(() => {
    hasUserInteractedWithInlineOffer.current = false

    if (!isInSample) return undefined

    if (!partnerId) {
      setWidgetState(
        isDevelopmentPreviewEnabled ? 'devPreview' : 'missingPartnerId',
      )
      return undefined
    }

    if (isEmbeddedMode && !containerElement) return undefined

    if (!thanksConfig) {
      setWidgetState(
        isDevelopmentPreviewEnabled ? 'devPreview' : 'missingPartnerId',
      )
      return undefined
    }

    try {
      configureThanksWidget(thanksConfig)
      displayThanksWidget()
      applyThanksPresentationGuards()
      setWidgetState('loaded')
    } catch {
      setWidgetState(isDevelopmentPreviewEnabled ? 'devPreview' : 'missingSdk')
      return undefined
    }

    const overlayObserver = new MutationObserver(() => {
      applyThanksPresentationGuards()
    })

    if (document.body) {
      overlayObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'src'],
      })
    }

    const overlayScanInterval = window.setInterval(() => {
      applyThanksPresentationGuards()
    }, 500)

    window.addEventListener('resize', applyThanksPresentationGuards)

    const markInlineInteraction = () => {
      hasUserInteractedWithInlineOffer.current = true
    }

    if (isEmbeddedMode && containerElement) {
      containerElement.addEventListener('pointerdown', markInlineInteraction, {
        capture: true,
      })
      containerElement.addEventListener('click', markInlineInteraction, {
        capture: true,
      })
      containerElement.addEventListener('focusin', markInlineInteraction)
      containerElement.addEventListener('keydown', markInlineInteraction, {
        capture: true,
      })
    }

    return () => {
      if (isEmbeddedMode && containerElement) {
        containerElement.removeEventListener(
          'pointerdown',
          markInlineInteraction,
          {
            capture: true,
          },
        )
        containerElement.removeEventListener('click', markInlineInteraction, {
          capture: true,
        })
        containerElement.removeEventListener('focusin', markInlineInteraction)
        containerElement.removeEventListener('keydown', markInlineInteraction, {
          capture: true,
        })
      }
      window.removeEventListener('resize', applyThanksPresentationGuards)
      window.clearInterval(overlayScanInterval)
      overlayObserver.disconnect()
      try {
        closeThanksWidget()
      } catch {
        // Widget is additive; cleanup failures should not affect the page.
      }
    }
  }, [
    containerElement,
    isEmbeddedMode,
    isInSample,
    partnerId,
    applyThanksPresentationGuards,
    thanksConfig,
  ])

  if (!isInSample) return null

  if (widgetState === 'devPreview') {
    return (
      <Alert variant="blueWithBg" mt={5}>
        <ThanksEmbedContainer
          className={className}
          ref={setContainerRef}
          data-testid="thanks-widget-embed"
        >
          <ThanksDevPreviewCard data-testid="thanks-widget-dev-preview">
            <ThanksDevPreviewTitle>
              Thanks offers (dev preview)
            </ThanksDevPreviewTitle>
            <ThanksDevPreviewDescription>
              Live SDK is unavailable in this environment, so this preview
              displays representative offers for the current placement.
            </ThanksDevPreviewDescription>
            <ThanksDevPreviewOfferList>
              {DEV_PREVIEW_OFFERS[placement].map((offer) => (
                <ThanksDevPreviewOfferItem key={offer}>
                  {offer}
                </ThanksDevPreviewOfferItem>
              ))}
            </ThanksDevPreviewOfferList>
          </ThanksDevPreviewCard>
        </ThanksEmbedContainer>
      </Alert>
    )
  }

  if (widgetState === 'missingPartnerId' || widgetState === 'missingSdk')
    return null

  if (!isEmbeddedMode) return null

  return (
    <ThanksEmbedSection>
      <ThanksEmbedContainer
        className={className}
        ref={setContainerRef}
        data-testid="thanks-widget-embed"
      />
    </ThanksEmbedSection>
  )
}
